import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// Preserve one line of cell content (min-h-8), its 24px padding, and the row border.
const minimumHeight = 57

export default function TableRowResizeHandle({
  rowNumber,
  height,
  measuredHeight,
  disabled,
  onResize,
}: {
  rowNumber: number
  height?: number
  measuredHeight: number
  disabled: boolean
  onResize: (height: number) => void
}) {
  const handle = useRef<HTMLDivElement>(null)
  const drag = useRef<{ pointerId: number; y: number; height: number; bounds: DOMRect } | null>(null)
  const [offset, setOffset] = useState<number | null>(null)
  const currentHeight = () => height ?? handle.current?.closest('tr')?.getBoundingClientRect().height ?? minimumHeight
  const resizedHeight = (start: { y: number; height: number }, y: number) => Math.max(minimumHeight, Math.round(start.height + y - start.y))

  return (
    <>
      {offset !== null &&
        drag.current &&
        createPortal(
          <div aria-hidden className="fixed inset-0 z-[1001] cursor-row-resize select-none">
            <div
              className="absolute h-1 bg-brand-neutral-400"
              style={{ left: drag.current.bounds.left, top: drag.current.bounds.top + offset, width: drag.current.bounds.width }}
            />
          </div>,
          document.body
        )}
      <div
        ref={handle}
        role="separator"
        aria-label={`Resize row ${rowNumber}`}
        aria-orientation="horizontal"
        aria-valuemin={minimumHeight}
        aria-valuenow={Math.round(height ?? measuredHeight)}
        aria-valuetext={`${Math.round(height ?? measuredHeight)} pixels`}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        data-table-copy-ignore
        className={`absolute bottom-0 left-0 z-30 h-1 w-full cursor-row-resize touch-none select-none focus-visible:outline-hidden ${
          offset === null ? 'hover:bg-brand-neutral-400 focus-visible:bg-brand-neutral-400' : ''
        }`}
        onPointerDown={(event) => {
          if (disabled || event.button !== 0 || !event.isPrimary) return
          event.preventDefault()
          event.stopPropagation()
          event.currentTarget.setPointerCapture(event.pointerId)
          drag.current = { pointerId: event.pointerId, y: event.clientY, height: currentHeight(), bounds: event.currentTarget.getBoundingClientRect() }
          setOffset(0)
        }}
        onPointerMove={(event) => {
          const start = drag.current
          if (!start || start.pointerId !== event.pointerId) return
          setOffset(resizedHeight(start, event.clientY) - start.height)
        }}
        onPointerUp={(event) => {
          const start = drag.current
          if (!start || start.pointerId !== event.pointerId) return
          drag.current = null
          setOffset(null)
          event.currentTarget.releasePointerCapture(event.pointerId)
          onResize(resizedHeight(start, event.clientY))
        }}
        onPointerCancel={() => {
          drag.current = null
          setOffset(null)
        }}
        onLostPointerCapture={() => {
          drag.current = null
          setOffset(null)
        }}
        onKeyDown={(event) => {
          if (disabled || !['ArrowUp', 'ArrowDown'].includes(event.key)) return
          event.preventDefault()
          event.stopPropagation()
          onResize(Math.max(minimumHeight, Math.round(currentHeight() + (event.key === 'ArrowDown' ? 10 : -10))))
        }}
      />
    </>
  )
}
